import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { format } from "date-fns";

import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import {
  useGetMyDisputeQuery,
  type DisputeStatus,
} from "@/services/(company)/payrollDisputesApi";
import { DisputeDetailCard } from "@/components/payroll-disputes/DisputeDetailCard";

const STATUS_TONE: Record<DisputeStatus, string> = {
  PENDING: "bg-yellow-light-3 text-yellow-700",
  UNDER_REVIEW: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

const disputeStatusLabel = (
  status: DisputeStatus,
  translate: (key: string) => string,
) => {
  const KEY: Record<DisputeStatus, string> = {
    PENDING: "ui.pending",
    UNDER_REVIEW: "company:payrolls.under_review",
    RESOLVED: "ui.resolved",
    REJECTED: "labels.rejected",
  };
  return translate(KEY[status]);
};

export const MySpaceDisputeDetail = () => {
  const { t } = useTranslation("my-space");
  usePageTitle(withBranding(t("page_titles.dispute_detail")));
  const navigate = useNavigate();
  const { id } = useParams();
  const disputeId = Number(id);
  const skip = !disputeId || Number.isNaN(disputeId);

  const { data, isLoading } = useGetMyDisputeQuery(disputeId, { skip });
  const dispute = data?.data;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xs border border-borderSecondary bg-white p-8 text-sm text-gray-400 text-center">
          {t("ui.loading_dispute")}
        </div>
      </div>
    );
  }
  if (!dispute) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xs border border-borderSecondary bg-white p-8 text-sm text-gray-400 text-center">
          {t("ui.dispute_not_found")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 rounded-xs border border-borderSecondary bg-white px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.HOME.PAYROLL.DISPUTES)}
            className="border border-card-background rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl-flip" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {t("my-space:ui.dispute_number", { id: dispute.id })}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>{t("labels.my_space")}</span>
              <span>/</span>
              <span>{t("labels.payroll")}</span>
              <span>/</span>
              <span>{t("labels.disputes")}</span>
              <span>/</span>
              <span className="text-gray-700 font-medium">
                {dispute.payrollRun?.periodStart
                  ? format(new Date(dispute.payrollRun.periodStart), "MMM yyyy")
                  : ""}
              </span>
            </div>
          </div>
        </div>
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-1 rounded-xs",
            STATUS_TONE[dispute.status],
          )}
        >
          {disputeStatusLabel(dispute.status, t)}
        </span>
      </div>

      <DisputeDetailCard dispute={dispute} />
    </div>
  );
};
